import React from 'react';
import PropTypes from 'prop-types';
import bemify from '../../utils/bemUtils';

/**
 * Represents the supported shimmer lengths.
 */
export class ShimmerLength {
  /**
   * Indicates a shimmer that is twenty-five percent in length.
   */
  static percent25 = 25;

  /**
   * Indicates a shimmer that is fifty percent in length.
   */
  static percent50 = 50;

  /**
   * Indicates a shimmer that is seventy-five percent in lenght.
   */
  static percent75 = 75;

  /**
   * Indicates a shimmer that is one-hundred percent in lenght.
   */
  static percent100 = 100;
}

/**
 * Represents the supported shimmer sizes.
 */
export class ShimmerSize {
  /**
   * Indicates an extra large shimmer size.
   */
  static extraLarge = 'extra-large';

  /**
   * Indicates a large shimmer size.
   */
  static large = 'large';

  /**
   * Indicates a normal shimmer size.
   */
  static normal = 'normal';

  /**
   * Indicates a large shimmer size.
   */
  static small = 'small';

  /**
   * Indicates an extra small shimmer size.
   */
  static extraSmall = 'extra-small';
}

/**
 * Represents the supported shimmer types.
 */
export class ShimmerType {
  /**
   * Indicates a line shimmer often used for text.
   */
  static line = 'line';

  /**
   * Indicates a round shimmer often used for images.
   */
  static round = 'round';

  /**
   * Indidcates a square shimmer used for big content.
   */
  static square = 'square';
}

/**
 * Represents a temporary loading animation meant to fill in for content.
 * @param {Object} props The component properties.
 */
export const Shimmer = (props) => {
  const { length, size, type } = props;
  const [block] = bemify('shimmer');

  return (
    <div
      className={block([
        type,
        type !== ShimmerType.round && type !== ShimmerType.square
          ? `w${length}`
          : '',
        type !== ShimmerType.square && size ? size : '',
      ])}
    ></div>
  );
};

Shimmer.propTypes = {
  length: PropTypes.oneOf([
    ShimmerLength.percent25,
    ShimmerLength.percent50,
    ShimmerLength.percent75,
    ShimmerLength.percent100,
  ]),
  size: PropTypes.oneOf([
    ShimmerSize.extraSmall, // only for round
    ShimmerSize.small,
    ShimmerSize.normal,
    ShimmerSize.large,
    ShimmerSize.extraLarge,
  ]),
  type: PropTypes.oneOf([
    ShimmerType.line,
    ShimmerType.round,
    ShimmerType.square,
  ]),
};

Shimmer.defaultProps = {
  length: ShimmerLength.percent25,
  size: ShimmerSize.normal,
  type: ShimmerType.line,
};
